import { useEffect, useMemo, useState } from 'react';
import { useObjectVal } from 'react-firebase-hooks/database';
import { ref } from '../../utils/firebase';
import type { AccountsTablePersistedPayload } from './accountsPreferences';

/** Normalized prefs passed into useAccountsTablePrefsSync (merge of RTDB + defaults). */
export type FrozenAccountsTablePrefs = {
  activeTab: number;
  filtersOpen: boolean;
  table: {
    filters: Array<{ id: string; value: unknown }>;
    sortBy: Array<{ id: string; desc: boolean }>;
    pageIndex: number;
    pageSize: number;
    globalFilter: string;
  };
};

type DefaultTableShape = {
  sortBy: Array<{ id: string; desc: boolean }>;
  pageSize: number;
  pageIndex: number;
};

function mergePrefs(
  saved: unknown,
  defaults: DefaultTableShape,
  options?: { defaultFiltersOpen?: boolean }
): FrozenAccountsTablePrefs {
  const s = (saved || {}) as AccountsTablePersistedPayload;
  const defaultFiltersOpen = options?.defaultFiltersOpen ?? true;
  return {
    activeTab:
      typeof s.activeTab === 'number' && s.activeTab >= 0 ? s.activeTab : 0,
    filtersOpen:
      typeof s.filtersOpen === 'boolean' ? s.filtersOpen : defaultFiltersOpen,
    table: {
      filters: Array.isArray(s.filters) ? s.filters : [],
      sortBy:
        Array.isArray(s.sortBy) && s.sortBy.length > 0
          ? s.sortBy
          : defaults.sortBy,
      pageSize:
        typeof s.pageSize === 'number' ? s.pageSize : defaults.pageSize,
      pageIndex:
        typeof s.pageIndex === 'number' ? s.pageIndex : defaults.pageIndex,
      globalFilter: typeof s.globalFilter === 'string' ? s.globalFilter : '',
    },
  };
}

/**
 * Loads prefs from RTDB and recomputes when `savedPrefs` updates, so a late
 * snapshot (e.g. after hard refresh / cache) is not stuck forever on a null read.
 *
 * `loaded` flips true one microtask after loading completes so the Firebase hook
 * has a chance to deliver the first value before we treat prefs as "ready" and
 * start persisting defaults over good server data.
 */
export function useFrozenAccountsTablePrefs(
  uid: string,
  tableName: string,
  userColsLoading: boolean,
  defaults: DefaultTableShape,
  options?: { defaultFiltersOpen?: boolean }
): { frozenPrefs: FrozenAccountsTablePrefs | null; prefsReady: boolean } {
  const prefsRef = useMemo(
    () => ref(`user_preferences/${uid}/accounts/tables/${tableName}`),
    [uid, tableName]
  );
  const [savedPrefs, prefsLoading] = useObjectVal(prefsRef as any);

  const [loaded, setLoaded] = useState(false);

  // Defer prefsReady one microtask after RTDB finishes loading so we do not snapshot
  // null and then overwrite good server data with defaults on hard refresh.
  useEffect(() => {
    if (userColsLoading || prefsLoading) {
      setLoaded(false);
      return;
    }
    let cancelled = false;
    if (typeof queueMicrotask === 'function') {
      queueMicrotask(() => {
        if (!cancelled) {
          setLoaded(true);
        }
      });
    } else {
      Promise.resolve().then(() => {
        if (!cancelled) {
          setLoaded(true);
        }
      });
    }
    return () => {
      cancelled = true;
    };
  }, [userColsLoading, prefsLoading]);

  const prefsReady = loaded && !userColsLoading && !prefsLoading;

  const frozenPrefs = useMemo((): FrozenAccountsTablePrefs | null => {
    if (!prefsReady) {
      return null;
    }
    return mergePrefs(savedPrefs, defaults, options);
  }, [
    prefsReady,
    savedPrefs,
    defaults.sortBy,
    defaults.pageSize,
    defaults.pageIndex,
    options?.defaultFiltersOpen,
  ]);

  return {
    frozenPrefs,
    prefsReady: frozenPrefs !== null,
  };
}
